// Pool of decode workers (§7: 2). Each task goes to the worker with the fewest jobs in flight.
import * as Comlink from 'comlink';
import type { DecodeWorkerApi } from './decode.worker';
import type { DecodedImage } from './types';
import { guard } from '../workerGuard';

type Slot = { worker: Worker; api: Comlink.Remote<DecodeWorkerApi>; busy: number };

export class DecodePool {
  private slots: Slot[] = [];
  constructor(size = 2) {
    for (let i = 0; i < size; i++) {
      this.slots.push(this.spawn(i));
    }
  }
  /** A decode worker whose calls reject if it fails to load; the slot then gets a fresh one (S13). */
  private spawn(i: number): Slot {
    const worker = new Worker(new URL('./decode.worker.ts', import.meta.url), {
      type: 'module',
      name: `decode-${i}`,
    });
    const slot: Slot = {
      worker,
      api: guard(worker, Comlink.wrap<DecodeWorkerApi>(worker), () => {
        worker.terminate();
        const k = this.slots.indexOf(slot);
        if (k >= 0) this.slots[k] = this.spawn(i);
      }),
      busy: 0,
    };
    return slot;
  }
  private async run<R>(fn: (api: Comlink.Remote<DecodeWorkerApi>) => Promise<R>): Promise<R> {
    const slot = this.slots.reduce((a, b) => (b.busy < a.busy ? b : a));
    slot.busy++;
    try {
      return await fn(slot.api);
    } finally {
      slot.busy--;
    }
  }
  decode(file: Blob) {
    return this.run((api) => api.decode(file) as Promise<DecodedImage>);
  }
  small(file: Blob, edge: number) {
    return this.run((api) => api.small(file, edge));
  }
  thumbnail(file: Blob) {
    return this.run((api) => api.thumbnail(file));
  }
  terminate() {
    for (const s of this.slots) s.worker.terminate();
    this.slots = [];
  }
}

let shared: DecodePool | null = null;
export const decodePool = () => (shared ??= new DecodePool(2));
